import { useCallback, useEffect, useRef, useState } from 'react';
import ApiClient from '../config/ApiClient';
import { Questions } from '../object-actions/types/types';

export type VoicePhase =
    | 'idle'
    | 'playing_question'
    | 'listening'
    | 'processing'
    | 'playing_reprompt'
    | 'error';

interface VoiceResponseData {
    success: boolean;
    transcript: string;
    parsed_value: number | null;
    action: 'next' | 'reprompt';
    not_understood_audio_url?: string | null;
    question_audio_url?: string | null;
}

interface UseVoiceAssessmentOptions {
    assignmentId: number | null;
    questions: Questions[];
    currentQuestionIndex: number;
    submitResponse: (assessmentQuestionId: number, response: number) => Promise<boolean>;
    goToNextQuestion: () => void;
    submitAssessment: () => Promise<boolean>;
    totalQuestions: number;
}

export function useVoiceAssessment({
    assignmentId,
    questions,
    currentQuestionIndex,
    submitResponse,
    goToNextQuestion,
    submitAssessment,
    totalQuestions,
}: UseVoiceAssessmentOptions) {
    const [voiceActive, setVoiceActive] = useState(false);
    const [phase, setPhase] = useState<VoicePhase>('idle');
    const [transcript, setTranscript] = useState<string | null>(null);
    const [voiceError, setVoiceError] = useState<string | null>(null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const stoppedByCodeRef = useRef(false);
    const isActiveRef = useRef(false);

    // Keep isActiveRef in sync so callbacks see current value
    useEffect(() => {
        isActiveRef.current = voiceActive;
    }, [voiceActive]);

    const cleanup = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            stoppedByCodeRef.current = true;
            mediaRecorderRef.current.stop();
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    const playAudio = useCallback((url: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (!isActiveRef.current) return resolve();
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => resolve();
            audio.onerror = () => reject(new Error('Audio playback failed'));
            audio.play().catch(reject);
        });
    }, []);

    /** Acquire the microphone early so it's warm when recording starts. */
    const acquireMic = useCallback(async (): Promise<MediaStream> => {
        if (streamRef.current && streamRef.current.active) {
            return streamRef.current;
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        return stream;
    }, []);

    /**
     * Start recording from a pre-acquired mic stream.
     * If no stream is provided, falls back to acquiring one on the spot.
     */
    const startRecording = useCallback((preAcquiredStream?: MediaStream): Promise<Blob> => {
        const SILENCE_THRESHOLD = 15;   // RMS volume level below which we consider silence (0-255 range)
        const SILENCE_DURATION = 750;   // ms of continuous silence after speech before auto-stop
        const MAX_RECORDING = 10000;    // absolute max recording time

        return new Promise(async (resolve, reject) => {
            try {
                const stream = (preAcquiredStream && preAcquiredStream.active)
                    ? preAcquiredStream
                    : await navigator.mediaDevices.getUserMedia({ audio: true });
                streamRef.current = stream;
                const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
                mediaRecorderRef.current = mediaRecorder;
                audioChunksRef.current = [];
                stoppedByCodeRef.current = false;

                mediaRecorder.ondataavailable = (event) => {
                    if (event.data.size > 0) {
                        audioChunksRef.current.push(event.data);
                    }
                };

                mediaRecorder.onstop = () => {
                    stream.getTracks().forEach(t => t.stop());
                    streamRef.current = null;
                    const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                    resolve(blob);
                };

                mediaRecorder.onerror = () => reject(new Error('Recording failed'));
                mediaRecorder.start();

                // Silence detection via Web Audio API AnalyserNode
                const audioContext = new AudioContext();
                const source = audioContext.createMediaStreamSource(stream);
                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 512;
                source.connect(analyser);
                const dataArray = new Uint8Array(analyser.frequencyBinCount);

                let speechDetected = false;
                let silenceStart: number | null = null;

                const checkSilence = () => {
                    if (mediaRecorder.state !== 'recording') {
                        audioContext.close();
                        return;
                    }

                    analyser.getByteFrequencyData(dataArray);
                    const rms = Math.sqrt(dataArray.reduce((sum, v) => sum + v * v, 0) / dataArray.length);

                    if (rms > SILENCE_THRESHOLD) {
                        speechDetected = true;
                        silenceStart = null;
                    } else if (speechDetected) {
                        if (silenceStart === null) {
                            silenceStart = Date.now();
                        } else if (Date.now() - silenceStart > SILENCE_DURATION) {
                            stoppedByCodeRef.current = true;
                            mediaRecorder.stop();
                            audioContext.close();
                            return;
                        }
                    }

                    requestAnimationFrame(checkSilence);
                };
                requestAnimationFrame(checkSilence);

                // Absolute max recording failsafe
                setTimeout(() => {
                    if (mediaRecorder.state === 'recording') {
                        stoppedByCodeRef.current = true;
                        mediaRecorder.stop();
                        audioContext.close();
                    }
                }, MAX_RECORDING);
            } catch (err) {
                reject(err);
            }
        });
    }, []);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            stoppedByCodeRef.current = true;
            mediaRecorderRef.current.stop();
        }
    }, []);

    const sendAudioToApi = useCallback(async (
        audioBlob: Blob,
        assessmentQuestionId: number,
    ): Promise<VoiceResponseData | null> => {
        if (!assignmentId) return null;

        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        formData.append('assessment_question_id', String(assessmentQuestionId));
        formData.append('assignment_id', String(assignmentId));
        formData.append('lang', navigator.language || 'en-US');

        const response = await ApiClient.post<VoiceResponseData>(
            '/api/assessments/voice-response',
            formData,
            { 'Content-Type': 'multipart/form-data' }
        );

        if (response.success && response.data) {
            return response.data;
        }
        throw new Error(response.error || 'Voice response failed');
    }, [assignmentId]);

    /** Run a single question cycle: play question audio -> record -> process -> advance or reprompt */
    const processQuestion = useCallback(async (questionIndex: number) => {
        if (!isActiveRef.current) return;

        const question = questions[questionIndex];
        if (!question || !question.assessment_question_id) return;

        setTranscript(null);
        setVoiceError(null);

        // Pre-acquire microphone so it's ready the instant the question finishes playing.
        // This runs in parallel with (or before) audio playback.
        let micStream: MediaStream | undefined;
        try {
            micStream = await acquireMic();
        } catch {
            // Will retry inside startRecording; don't block the flow here
        }

        // 1. Play the question audio (mic is already warming up)
        if (question.audio_url) {
            setPhase('playing_question');
            try {
                await playAudio(question.audio_url);
            } catch {
                // Continue even if audio playback fails
            }
        }

        if (!isActiveRef.current) return;

        // 2. Listen for the user's response (mic already acquired — no delay)
        setPhase('listening');
        let audioBlob: Blob;
        try {
            audioBlob = await startRecording(micStream);
        } catch (err: any) {
            setVoiceError('Microphone access denied. Please allow microphone permissions.');
            setPhase('error');
            setVoiceActive(false);
            return;
        }

        if (!isActiveRef.current) return;

        // 3. Send to API
        setPhase('processing');
        let result: VoiceResponseData | null;
        try {
            result = await sendAudioToApi(audioBlob, question.assessment_question_id);
        } catch (err: any) {
            setVoiceError(err.message || 'Failed to process voice response');
            setPhase('error');
            return;
        }

        if (!result || !isActiveRef.current) return;

        setTranscript(result.transcript);

        if (result.success && result.parsed_value !== null) {
            // 4a. Success — mirror locally and advance
            await submitResponse(question.assessment_question_id, result.parsed_value);

            if (questionIndex < totalQuestions - 1) {
                goToNextQuestion();
                // Directly process next question instead of relying on useEffect,
                // which would miss it because phase is still 'processing'.
                await processQuestion(questionIndex + 1);
            } else {
                // Last question — auto-submit
                setPhase('idle');
                await submitAssessment();
                setVoiceActive(false);
            }
        } else {
            // 4b. Re-prompt — play "not understood" audio only, then retry.
            // processQuestion will replay the question audio at the top of the loop.
            setPhase('playing_reprompt');
            try {
                if (result.not_understood_audio_url) {
                    await playAudio(result.not_understood_audio_url);
                }
            } catch {
                // continue even if playback fails
            }

            if (!isActiveRef.current) return;

            // Retry the same question (replays question audio at step 1)
            await processQuestion(questionIndex);
        }
    }, [questions, totalQuestions, acquireMic, playAudio, startRecording, sendAudioToApi, submitResponse, goToNextQuestion, submitAssessment]);

    // Kick off voice processing when voice mode is first activated
    useEffect(() => {
        if (voiceActive) {
            processQuestion(currentQuestionIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voiceActive]);

    const startVoiceMode = useCallback(() => {
        setVoiceActive(true);
        setVoiceError(null);
        setTranscript(null);
        setPhase('idle');
    }, []);

    const stopVoiceMode = useCallback(() => {
        setVoiceActive(false);
        setPhase('idle');
        setTranscript(null);
        cleanup();
    }, [cleanup]);

    return {
        voiceActive,
        phase,
        transcript,
        voiceError,
        startVoiceMode,
        stopVoiceMode,
        stopRecording,
    };
}
